// Chat panel: mounts the assistant-ui runtime provider and the hand-composed
// Thread. The visible chrome (header, drag, close button) is provided by the
// surrounding CopilotButton component; this file is just the conversation
// surface.

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { observer } from "mobx-react";
import { reaction, runInAction } from "mobx";
import { user_id } from "../userData";
import { CopilotRuntimeProvider } from "./CopilotRuntimeProvider";
import { Thread } from "./assistant-ui/Thread";
import { WorkflowLibrary } from "./workflows/WorkflowLibrary";
import { WorkflowParamModal } from "./workflows/WorkflowParamModal";
import { WorkflowRunMarker } from "./workflows/WorkflowRunMarker";
import { FloatingRecordingPill } from "./workflows/recording/FloatingRecordingPill";
import { RecordingWizard } from "./workflows/recording/RecordingWizard";
import { getWorkflow } from "./workflows/workflowsApi";
import "./copilot.css";
import "./copilot-theme.css";

/**
 * @param {{ controller: import("./CopilotController").CopilotController }} props
 */
export const ChatPanel = observer(function ChatPanel({ controller }) {
  // Pending workflow-start during the "end current session?" confirmation.
  // Non-null while the in-app modal is up. Mirrors the profile-switch
  // pattern in CopilotButton.jsx so we don't fall back to window.confirm.
  const [pendingRun, setPendingRun] = useState(null);

  // Attach the recording controller once both signals (guac + user_id) are
  // available. attachRecordingController() reads them from controller.iframe
  // + user_id.get() internally and is a no-op when either is missing.
  //
  // Two retry mechanisms because the two prerequisites arrive via different
  // channels:
  //   - user_id is observable, so a MobX reaction catches late arrival.
  //   - The Guacamole client is exposed via an iframe ref that isn't
  //     observable; iframe.onload happens outside React. A bounded poll
  //     covers the case where user_id is ready at mount but the iframe is
  //     still loading. Polling stops the moment attach succeeds.
  useEffect(() => {
    if (controller.attachRecordingController()) return;

    const disposeReaction = reaction(
      () => user_id.get(),
      () => controller.attachRecordingController(),
      { fireImmediately: false },
    );

    let attempts = 0;
    const MAX_ATTEMPTS = 30;  // 30 × 500ms = 15s — well past iframe load
    const pollId = setInterval(() => {
      if (controller.attachRecordingController() || ++attempts >= MAX_ATTEMPTS) {
        clearInterval(pollId);
      }
    }, 500);

    return () => { disposeReaction(); clearInterval(pollId); };
  }, [controller]);

  const surfaceValidationError = (workflow, params, err) => {
    if (err && err.errors) {
      runInAction(() => {
        controller.workflowParamPrompt = {
          workflow,
          paramValues: params,
          serverErrors: err.errors,
        };
      });
      return true;
    }
    return false;
  };

  const startRun = async (wf, params) => {
    runInAction(() => { controller.workflowParamPrompt = null; });
    controller.closeWorkflowsLibrary();
    const hasParams = (wf.parameters || []).length > 0;
    try {
      await controller.executeWorkflow(wf.id, params, { workflowName: wf.name });
    } catch (e) {
      if (hasParams && surfaceValidationError(wf, params, e)) return;
      // No params, or non-validation failure: alert. A 400 on a no-param
      // run-brief is a server-side bug, not anything the user can fix.
      window.alert(
        (e && e.errors)
          ? "Couldn't run the workflow:\n" + e.errors.map(x => `  ${x.path}: ${x.message}`).join("\n")
          : "Couldn't run the workflow. Try again."
      );
    }
  };

  const requestRun = (wf, params) => {
    // Close the param prompt as we hand off — otherwise it lingers under the
    // "Start workflow?" confirm (startRun also clears it on the direct path).
    runInAction(() => { controller.workflowParamPrompt = null; });
    if (controller.sessionId && !controller.busy) {
      setPendingRun({ wf, params });
      return;
    }
    startRun(wf, params);
  };

  // Run-Now callback delegates to the existing requestRun logic that
  // WorkflowLibrary already uses. SavedStage (Task 18) calls this with
  // the saved workflow's id.
  const onRunFromWizard = async (workflowId) => {
    controller.recordingController && controller.recordingController.closeWizard();
    const pb = await getWorkflow(workflowId);
    if ((pb.parameters || []).length === 0) {
      requestRun(pb, {});
      return;
    }
    runInAction(() => {
      controller.workflowParamPrompt = {
        workflow: pb,
        paramValues: null,
        serverErrors: null,
      };
    });
  };

  const rc = controller.recordingController;
  const isRecording = rc && rc.stage === "recording";
  const wizardOpen = rc && (rc.stage === "captured" || rc.stage === "compiling" || rc.stage === "reviewing" || rc.stage === "saved");

  return (
    <div className="copilot-root copilot-chat-panel">
      <WorkflowRunMarker
        currentWorkflow={controller.currentWorkflow}
        onDismiss={() => runInAction(() => { controller.currentWorkflow = null; })}
      />

      {/* Conversation body — always mounted. A floating panel hides itself
          entirely while recording (copilot-panel--recording), taking the
          Thread with it; a docked panel stays up, so the Thread stays visible
          next to the VM. Only the floating pill, portaled to <body> below,
          shows in the floating case. */}
      <CopilotRuntimeProvider controller={controller}>
        <Thread />
      </CopilotRuntimeProvider>

      {!isRecording && controller.workflowsLibraryOpen && (
        <WorkflowLibrary
          canRun={controller.canRunWorkflow}
          onRunWorkflow={async (id) => {
            // Close the library so the user lands back on the panel where the
            // run actually happens (or the param prompt takes over on top).
            controller.closeWorkflowsLibrary();
            const pb = await getWorkflow(id);
            if ((pb.parameters || []).length === 0) {
              requestRun(pb, {});
              return;
            }
            runInAction(() => {
              controller.workflowParamPrompt = {
                workflow: pb,
                paramValues: null,
                serverErrors: null,
              };
            });
          }}
          onClose={() => controller.closeWorkflowsLibrary()}
          recordingController={controller.recordingController}
        />
      )}

      {controller.workflowParamPrompt && (
        <WorkflowParamModal
          workflow={controller.workflowParamPrompt.workflow}
          serverErrors={controller.workflowParamPrompt.serverErrors}
          onSubmit={(params) => {
            const wf = controller.workflowParamPrompt.workflow;
            requestRun(wf, params);
          }}
          onCancel={() => runInAction(() => { controller.workflowParamPrompt = null; })}
        />
      )}

      {pendingRun && (
        <div
          className="copilot-modal-backdrop"
          onClick={() => setPendingRun(null)}
        >
          <div
            role="alertdialog"
            aria-labelledby="copilot-workflow-confirm-title"
            aria-describedby="copilot-workflow-confirm-body"
            className="copilot-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="copilot-workflow-confirm-title" className="copilot-modal-title">
              Start workflow?
            </h3>
            <p id="copilot-workflow-confirm-body" className="copilot-modal-body">
              Running <strong>{pendingRun.wf.name}</strong> will end your current
              Copilot session. The current chat history will be cleared.
            </p>
            <div className="copilot-modal-buttons">
              <button
                type="button"
                className="copilot-modal-btn copilot-modal-btn--ghost"
                onClick={() => setPendingRun(null)}
                autoFocus
              >
                Cancel
              </button>
              <button
                type="button"
                className="copilot-modal-btn copilot-modal-btn--primary"
                onClick={() => {
                  const { wf, params } = pendingRun;
                  setPendingRun(null);
                  startRun(wf, params);
                }}
              >
                Start &amp; clear chat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pill must portal to <body> because CopilotPanel uses transform,
          which would otherwise scope position:fixed to the panel. */}
      {isRecording && createPortal(
        <FloatingRecordingPill controller={rc} />,
        document.body,
      )}

      {/* Wizard renders inside the panel; it's intentionally modal-within-
          panel, not viewport-modal. */}
      {wizardOpen && (
        <RecordingWizard controller={rc} onRunWorkflow={onRunFromWizard} />
      )}
    </div>
  );
});
